import mixpanel from "mixpanel-browser";
import React, { FunctionComponent, ReactNode, useCallback, useState } from "react";

import Button, { Variant } from "../../../../../../shared/components/design-system/Button";
import Dialog from "../../../../../../shared/components/design-system/Dialog";
import { IconSlug } from "../../../../../../shared/components/design-system/Icon";
import {
  SectionHeader,
  StandardFormFooter,
} from "../../../../../../shared/components/design-system/component-groups/section-header-footer";
import { Translatable } from "../../../../../../shared/components/translation";

interface ConfirmDeleteButtonProps {
  title: string;
  confirmBody: string | ReactNode;
  confirmText: string;
  track?: string;
  onConfirm: () => void;
  label: Translatable;
  variant?: Variant;
  loading?: boolean;
  size?: "small" | "medium" | "large" | "fill";
  disabled?: boolean;
  icon?: IconSlug;
}

const ConfirmDeleteButton: FunctionComponent<ConfirmDeleteButtonProps> = ({
  title,
  confirmBody,
  confirmText,
  track,
  onConfirm,
  label,
  variant,
  loading,
  size,
  disabled,
  icon,
}) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const openConfirmModal = useCallback(() => {
    if (track) {
      mixpanel.track(`Opened the ${track} delete modal`);
    }
    setModalOpen(true);
  }, [track]);

  const closeConfirmModal = useCallback(() => {
    if (track) {
      mixpanel.track(`Closed the ${track} delete modal`);
    }
    setModalOpen(false);
  }, [track]);

  const handleConfirm = useCallback(() => {
    if (track) {
      mixpanel.track(`${track} delete modal confirm button clicked`);
    }
    setModalOpen(false);
    onConfirm();
  }, [onConfirm, track]);

  return (
    <>
      <Dialog isOpen={modalOpen} onClose={closeConfirmModal}>
        <SectionHeader title={title} />
        {typeof confirmBody === "string" ? <p>{confirmBody}</p> : confirmBody}
        <StandardFormFooter>
          <Button label="Cancel" onClick={closeConfirmModal} />
          <Button label={confirmText} variant="danger" onClick={handleConfirm} />
        </StandardFormFooter>
      </Dialog>

      <Button
        variant={variant}
        disabled={disabled}
        size={size}
        label={label}
        loading={loading}
        onClick={openConfirmModal}
        icon={icon}
      />
    </>
  );
};

export default ConfirmDeleteButton;
